'use client';

import { Modal } from '@magicschool/spellbook/components/Modal';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useCallback, useState } from 'react';
import { FilePreviewDocumentSlot, FilePreviewExpandedSlot, FilePreviewImageSlot } from './slots';
import type { FilePreviewBaseTextProps, ViewerEntry } from './types';

export type { FilePreviewDocumentProps, FilePreviewImageProps } from './slots';
export type { FilePreviewBaseTextProps, FilePreviewChipBaseProps } from './types';

export interface FilePreviewProps extends React.ComponentProps<'div'> {
  /**
   * Shared text strings for all chip children. Merged into each chip with per-chip `text` taking
   * priority. Pass translated strings here once rather than repeating on every chip.
   */
  text?: FilePreviewBaseTextProps;
  /** Disables all chip interactions in the tray. ORed with per-chip `disabled`. */
  disabled?: boolean;
}

const FilePreviewBase = ({ children, className, text, disabled, ...rest }: FilePreviewProps) => {
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState<ViewerEntry | null>(null);

  const openModal = useCallback((newEntry: ViewerEntry) => {
    setEntry(newEntry);
    setOpen(true);
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setEntry(null);
  }, []);

  const { orderedSlots } = useOrderedSlots(children, {
    image: { Component: FilePreviewImageSlot, props: { openModal, isModalOpen: open, parentText: text, parentDisabled: disabled } },
    document: { Component: FilePreviewDocumentSlot, props: { openModal, isModalOpen: open, parentText: text, parentDisabled: disabled } },
  });

  return (
    <>
      <div data-spellbook-name="file-preview" className={cn('flex flex-wrap gap-3 items-start', className)} {...rest}>
        {mapOrderedSlots(orderedSlots)}
      </div>
      <Modal open={open} onOpenChange={handleOpenChange} size="lg">
        {entry && (
          <>
            <Modal.Header>{entry.kind === 'image' ? entry.title : entry.name}</Modal.Header>
            <Modal.Content className="p-3">{entry.expandedContent}</Modal.Content>
          </>
        )}
      </Modal>
    </>
  );
};
FilePreviewBase.displayName = 'FilePreview';

/**
 * Attachment preview tray for chat input surfaces. Wraps staged or sent file chips in a flex
 * row and provides a shared Viewer modal that image and document chips can open on click.
 *
 * ### When to use
 * - Displaying files attached to a chat message before or after sending
 * - Showing upload progress, abort controls, and completion announcements for each file
 * - Letting users remove or expand individual attachments
 *
 * ### When NOT to use
 * - For full file-management lists with download actions: use `FileItem` directly
 * - For standalone image galleries or lightboxes outside a chat context
 *
 * ### API
 * - `text`: Shared translated strings injected into all chip children. Per-chip `text` takes priority.
 * - `disabled`: Disables all chip interactions in the tray (ORed with per-chip `disabled`). Useful for locking the tray during form submission.
 * - `className`: Merged onto the flex layout `<div>` — use to override spacing or wrap behaviour
 * - All other native `<div>` props (`id`, `aria-*`, `data-*`, etc.) are forwarded to the layout `<div>`
 *
 * ### Composition (slots)
 * - `<FilePreview.Image>`: 44×44 thumbnail chip. Accepts `expandOnClick` to open the Viewer modal.
 *   Supports upload processing state (dim + spinner + abort button). Optional `<FilePreview.Expanded>`
 *   child overrides the default full-size image shown in the modal.
 * - `<FilePreview.Document>`: File-type-badged card chip. Accepts `expandOnClick` to open the Viewer
 *   modal. Supports processing state and word count. Optional `<FilePreview.Expanded>` child overrides
 *   the default `FileItem` card shown in the modal.
 * - `<FilePreview.Expanded>`: Slot placed inside an Image or Document chip to inject a custom expanded
 *   view (e.g. a PDF viewer or pan/zoom image viewer) into the shared Viewer modal.
 *
 * ### Visual states
 * - **Default**: Thumbnail or file card with a hover-revealed remove button
 * - **Processing**: Dimmed thumbnail / spinner badge, always-visible abort button, no remove button
 * - **Disabled**: Remove, abort, and expand interactions are all suppressed
 * - **Error** (Document only): Chip unmounts — failed uploads are expected to be cleared by the consumer
 *
 * ### Accessibility
 * - Remove and abort buttons have accessible names composed from their action label and the file name/alt
 * - `expandOnClick` chips are keyboard-focusable buttons (click, Enter, Space)
 * - The Viewer modal traps focus on open and returns focus to the trigger chip on close
 * - Upload completion is announced via an `aria-live="polite"` region that self-clears after 4 s
 *
 * ```tsx
 * <FilePreview>
 *   <FilePreview.Image src={url} alt="Lesson map" expandOnClick onRemove={onRemove} />
 *   <FilePreview.Document name="Lesson.pdf" fileType="pdf" wordCount={1420} expandOnClick onRemove={onRemove}>
 *     <FilePreview.Expanded>
 *       <MyPdfViewer src={url} />
 *     </FilePreview.Expanded>
 *   </FilePreview.Document>
 * </FilePreview>
 * ```
 */
export const FilePreview = Object.assign(FilePreviewBase, {
  Image: FilePreviewImageSlot,
  Document: FilePreviewDocumentSlot,
  Expanded: FilePreviewExpandedSlot,
});
